import { FormInstance } from "antd";
import { OrderExportFormValues } from "./components/OrderExportForm/type";
import { computed, observable, makeObservable, actionBound } from "mobx";
import {
    getOrderExportFieldsStatus,
    getOrderExportList,
    OrderExportResult,
} from "@/services/api/moreFeatures/orderExport";
import OrderExportField from "@/services/api/moreFeatures/orderExport/enum/Fields";
import GlobalStore from "@/store";
import { cloneDeep, difference, omit } from "lodash";
import { completeExportFields, defaultOrderExportFields, productFieldValueArray } from "./util";
import OrderStatus from "@/enum/OrderStatus";
import WaybillPrinted from "@/services/api/moreFeatures/orderExport/enum/WaybillPrinted";
import moment from "moment";
import ExportType from "@/services/api/moreFeatures/orderExport/enum/ExportType";
import OrderType from "@/services/api/moreFeatures/orderExport/enum/OrderType";
import ShopType from "@/enum/ShopType";
import { useContext } from "react";
import React from "react";
import SupplierPartnershipsStore from "@/store/SupplierPartnershipsStore";
import OrderItemStyle from "@/services/api/moreFeatures/orderExport/enum/OrderItemStyle";
import RetailerPartnershipsStore from "@/store/RetailerPartnershipsStore";

const ORDER_EXPORT_KEY = "ORDER_EXPORT_KEY";

class OrderExportStore {
    supplierPartnershipStore = new SupplierPartnershipsStore();
    retailerPartnershipsStore = new RetailerPartnershipsStore();

    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            initOrderExportFields: actionBound,
            orderExportFields: observable,
            updateOrderExportFields: actionBound,
            saveOrderExportFormValuesCache: actionBound,
            loadOrderExportFields: actionBound,
            loading: observable,
            pageSize: observable,
            pageNo: observable,
            total: observable,
            orderExportResults: observable,
            pagination: computed,
            refresh: actionBound,
            onSearch: actionBound,
        });
        this.initOrderExportFields();
    }

    form: FormInstance<OrderExportFormValues>;

    injectForm(form: FormInstance<OrderExportFormValues>) {
        this.form = form;
    }

    initOrderExportFields() {
        /** 默认顺序的导出项 */
        this.orderExportFields = [...defaultOrderExportFields];
    }

    orderExportFields: OrderExportField[] = [];

    updateOrderExportFields(orderExportFields: OrderExportField[]) {
        this.orderExportFields = orderExportFields;
    }

    saveOrderExportFormValuesCache(values: OrderExportFormValues) {
        const cloneValue = omit(cloneDeep(values), [
            "timeRange",
            "captchaText",
            "supplierIds",
            "retailerShopId",
            "retailerShopIds",
        ]);
        const storageValue = JSON.stringify(cloneValue);

        localStorage.setItem(ORDER_EXPORT_KEY, storageValue);
    }

    async loadOrderExportFields() {
        const saveFieldsStatuses = await getOrderExportFieldsStatus();
        const orderExportFields = completeExportFields(saveFieldsStatuses);
        /** 选中的值 */
        let checkedOrderExportFields = saveFieldsStatuses.filter(({ checked }) => checked).map(({ field }) => field);
        /** 列表中顺序的值 */
        this.orderExportFields = orderExportFields;

        const defaultValue = {
            orderStatus: [OrderStatus.SUCCESS, OrderStatus.SHIPPED],
            waybillPrinted: WaybillPrinted.ALL,
            sellerFlags: [],
            needExportingOrderItems: true,
            exportType: ExportType.SHIPPING_TIME,
            needExportRefundOrderItems: false,
            timeRange: [moment().startOf("day"), moment()],
            shopIds: [],
            fields: checkedOrderExportFields,
            orderType: OrderType.ALL,
            orderItemStyle: OrderItemStyle.PER_ITEM_PER_LINE,
        };

        try {
            const storeValues = JSON.parse(localStorage.getItem(ORDER_EXPORT_KEY));

            if (!storeValues) return defaultValue;

            /** 忽略代理商的缓存 */
            delete storeValues.retailerShopId;
            delete storeValues.supplierIds;

            const cacheShopId = storeValues?.shopIds?.[0];

            /**
             * 获取导出类型（需兼容旧版本缓存数据）
             * @description
             * - `timeType` 是已废弃字段，当前版本应从 `exportType` 获取
             * - 为兼容用户本地缓存中可能存在的旧数据格式，保留临时回退逻辑
             */
            let exportType = storeValues.exportType || storeValues?.timeType;
            const orderStatus = storeValues?.orderStatus;
            const shopIds = [];
            if (this.globalStore.allowedShops.includes(cacheShopId)) {
                shopIds.push(cacheShopId);

                const shop = this.globalStore.shopMapping.get(cacheShopId);

                if (
                    shop?.type !== ShopType.MANUAL &&
                    shop?.type !== ShopType.INDEPENDENT_MANUAL &&
                    exportType === ExportType.EXPORT_TIME
                ) {
                    exportType = defaultValue.exportType;
                }

                if (orderStatus.includes(OrderStatus.PENDING_SHIPMENT) && exportType === ExportType.SHIPPING_TIME) {
                    exportType = ExportType.ORDER_TIME;
                }
            }

            /** 不导出商品信息需要移除商品信息相关的字段 */
            if (storeValues?.needExportingOrderItems === false) {
                checkedOrderExportFields = difference(checkedOrderExportFields, productFieldValueArray);
            }

            return {
                orderItemStyle: OrderItemStyle.PER_ITEM_PER_LINE,
                timeRange: [moment().startOf("day"), moment()],
                needExportRefundOrderItems: false,
                orderType: OrderType.ALL,
                ...storeValues,
                fields: checkedOrderExportFields,
                exportType,
                shopIds,
            };
        } catch {
            return defaultValue;
        }
    }

    /** ----------------列表相关--------------------------------- */

    loading = false;

    pageSize = 10;

    pageNo = 1;

    total = 0;

    orderExportResults: OrderExportResult[] = [];

    get pagination() {
        return {
            current: this.pageNo,
            pageSize: this.pageSize,
            total: this.total,
            onChange: this.onSearch,
        };
    }

    async refresh() {
        await this.onSearch();
    }

    async onSearch(pageNo = this.pageNo, pageSize = this.pageSize) {
        this.loading = true;

        try {
            this.loading = true;
            this.pageNo = pageNo;
            this.pageSize = pageSize;

            const { items, totalCount } = await getOrderExportList({
                pageNo,
                pageSize,
            });

            this.orderExportResults = items;
            this.total = totalCount;

            const { pendingSupplierShopIds, pendingRetailerShopIds } = items.reduce<{
                pendingSupplierShopIds: Set<number>;
                pendingRetailerShopIds: Set<number>;
            }>(
                (acc, { retailerShopId, shopIds, supplierIds }) => {
                    // 1) 收集所有需要加载「分销合作关系」的供货商 shopId
                    if (retailerShopId) {
                        if (shopIds.length === 1) {
                            acc.pendingSupplierShopIds.add(shopIds[0]);
                        }
                    }

                    // 2) 收集所有需要加载「分销合作关系」的代理商 shopId
                    if (supplierIds?.length && shopIds?.length) {
                        shopIds.forEach((id) => acc.pendingRetailerShopIds.add(id));
                    }

                    return acc;
                },
                {
                    // 收集所有需要加载分销合作关系的供货商店铺ID
                    pendingSupplierShopIds: new Set<number>(),
                    // 收集所有需要加载分销合作关系的代理商店铺ID
                    pendingRetailerShopIds: new Set<number>(),
                },
            );

            pendingSupplierShopIds.forEach(this.supplierPartnershipStore.ensureEstablishedPartnerships);
            pendingRetailerShopIds.forEach(this.retailerPartnershipsStore.ensureEstablishedPartnerships);
        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
        }
    }
}

export const OrderExportStoreContext = React.createContext<OrderExportStore>(undefined);

export const useOrderExportStore = () => {
    return useContext(OrderExportStoreContext);
};

export default OrderExportStore;
